import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Heading, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { useSiteContext, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { Notification, Pictogram, variants } from '@eti/components';
import { WarningCircle } from '@phosphor-icons/react';
import { PartOfExternalFlights, TripType } from '@eti/schema-types';
import Tabs from '../../../../my-bookings/common/Tabs';
import Banner from '../../../../../common/components/Banner';
import { routes } from '../../../../../constants/routesAndApisConstants';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import { getBaggageGDSInfoPerTraveler } from '../utils';
import { getBaggageIdsAvailableToBuy, hasDifferencesBetweenBounds } from './utils';
import BoundBaggageInfo from './BoundBaggageInfo';
import type { BoundBaggageInfoType } from './models';

const inlineStyles = css`
  align-items: baseline;
  padding: 16px 8px 16px 16px;
`;

interface BaggageDrawerProps {
  baggagePerBound: BoundBaggageInfoType[];
}

const BaggageDrawer = ({ baggagePerBound }: BaggageDrawerProps) => {
  const { t } = useTranslation();
  const { brand } = useSiteContext();
  const {
    addOnOrders,
    availableExtraProducts,
    journeyType,
    partOfExternalFlights,
    shouldShowUpgradeBanner,
    travelers,
  } = useOrderDetails();

  const baggageGDSInfoPerTraveler = useMemo(
    () => getBaggageGDSInfoPerTraveler(addOnOrders),
    [addOnOrders],
  );

  const hasBaggageDifferences = useMemo(
    () => hasDifferencesBetweenBounds(baggagePerBound),
    [baggagePerBound],
  );

  const tabItems = useMemo(
    () =>
      baggagePerBound.map(
        ({ baggageInfoPerTraveler, boundId, boundTitle, destination, origin }, boundIndex) => {
          const isReturnBound = journeyType === TripType.Return && boundIndex === 1;
          const shouldShowWarningIconInTab = isReturnBound && hasBaggageDifferences;
          return {
            title: (
              <Inline align="center" noWrap spacing={4}>
                {shouldShowWarningIconInTab && (
                  <WarningCircle
                    aria-labelledby="baggage-differences-notification"
                    color={vars.colors.signal.caution.light}
                    data-testid={`bound-baggage-${boundId}-warning-icon`}
                    role="img"
                    size={20}
                    weight="fill"
                  />
                )}
                {t(boundTitle, boundIndex + 1)}
              </Inline>
            ),
            content: (
              <BoundBaggageInfo
                baggageInfoPerTraveler={baggageInfoPerTraveler}
                boundId={boundId}
                destination={destination}
                isReturnBound={isReturnBound}
                key={boundId}
                origin={origin}
              />
            ),
          };
        },
      ),
    [hasBaggageDifferences, journeyType, t, baggagePerBound],
  );

  const baggageBannerLink = useMemo(() => {
    const baggageIdsAvailableToBuy = availableExtraProducts
      ? getBaggageIdsAvailableToBuy(availableExtraProducts)
      : [];
    if (baggageIdsAvailableToBuy.length > 0) {
      return baggageIdsAvailableToBuy.length === 1
        ? `${routes.ORDER_DETAILS_EXTRA_PRODUCTS}#product-id-${baggageIdsAvailableToBuy[0]}`
        : routes.ORDER_DETAILS_EXTRA_PRODUCTS;
    }
    return null;
  }, [availableExtraProducts]);

  return (
    <Stack>
      {partOfExternalFlights === PartOfExternalFlights.All && (
        <Notification dataTestId="ryanair-notification">
          <Text asHtml>{t('OrderDetails.RyanairFlight.Baggage.Drawer.Text')}</Text>
        </Notification>
      )}
      {hasBaggageDifferences && (
        <Notification
          contentId="baggage-differences-notification"
          dataTestId="baggage-differences-notification"
          variant={variants.WARNING}
        >
          {t('OrderDetails.Baggage.Drawer.DifferencesNotification')}
        </Notification>
      )}
      {journeyType === TripType.OneWay ? (
        <Card>
          <BoundBaggageInfo
            baggageInfoPerTraveler={baggagePerBound[0].baggageInfoPerTraveler}
            boundId={baggagePerBound[0].boundId}
            destination={baggagePerBound[0].destination}
            isReturnBound={false}
            key={baggagePerBound[0].boundId}
            origin={baggagePerBound[0].origin}
          />
        </Card>
      ) : (
        <Card padding={0}>
          <Tabs id="baggage-bounds" tabItems={tabItems} />
        </Card>
      )}
      {Object.keys(baggageGDSInfoPerTraveler).length > 0 && (
        <Card>
          <Stack>
            <Heading data-testid="baggage-gds-card-title" level={4}>
              {t('OrderDetails.BaggageGDS.Title')}
            </Heading>
            {travelers.map(
              (traveler) =>
                traveler.id &&
                baggageGDSInfoPerTraveler[traveler.id] && (
                  <Stack key={traveler.id} spacing={8}>
                    <Text
                      data-testid="baggage-gds-card-traveler-name"
                      variant={textVariants._14_BOLD}
                    >
                      {traveler.name}
                    </Text>
                    <Stack data-testid="baggage-gds-card-agent-notes" spacing={0}>
                      {baggageGDSInfoPerTraveler[traveler.id].map((agentNote, index) => (
                        <Text
                          data-testid={`note-${traveler.id}${index}`}
                          key={`note-${traveler.id}${index}`}
                        >
                          {agentNote}
                        </Text>
                      ))}
                    </Stack>
                  </Stack>
                ),
            )}
          </Stack>
        </Card>
      )}
      {shouldShowUpgradeBanner && (
        <Banner
          data-testid="add-baggage"
          description={
            baggageBannerLink
              ? t('OrderDetails.Baggage.AdditionalBaggagePanel.Description')
              : t('OrderDetails.Baggage.Drawer.AirportNotification')
          }
          footer={
            baggageBannerLink && (
              <Button as={Link} data-testid="add-baggage-banner-button" to={baggageBannerLink}>
                {t('OrderDetails.Baggage.AdditionalBaggagePanel.AddExtraBaggageButton.Label')}
              </Button>
            )
          }
          headingAs="h3"
          icon={
            <Inline className={inlineStyles} spacing={0}>
              <Pictogram
                brand={brand.code}
                data-testid="add-baggage-banner-icon"
                id="checked-baggage-new"
              />
              <Pictogram
                brand={brand.code}
                data-testid="add-baggage-banner-icon"
                height={42}
                id="cabin-baggage-new"
                width={24}
              />
            </Inline>
          }
          title={t('OrderDetails.Baggage.AdditionalBaggagePanel.Header')}
        />
      )}
    </Stack>
  );
};

export default BaggageDrawer;
